import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Phone, Mail, MapPin, Plus, CheckCircle, Clock, XCircle, Trash2 } from 'lucide-react';
import '../components/styles.css';

export default function Prospects() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    status: 'non_effectue',
    notes: '',
    service_interested: '',
    estimated_budget: '',
    source: 'telephone'
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        setUser(currentUser);
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        navigate('/crm/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchProspects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterStatus]);

  const fetchProspects = async () => {
    try {
      const supabase = createClient();
      
      let query = supabase
        .from('prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erreur lors de la récupération des prospects:', error);
        alert('Erreur lors de la récupération des prospects: ' + error.message);
      } else {
        setProspects(data || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des prospects:', error);
      alert('Erreur lors de la récupération des prospects: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supabase = createClient();

    if (editingProspect) {
      // Update existing prospect
      const { error } = await supabase
        .from('prospects')
        .update(formData)
        .eq('id', editingProspect.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour du prospect');
      } else {
        setShowForm(false);
        setEditingProspect(null);
        resetForm();
        fetchProspects();
      }
    } else {
      // Create new prospect
      const { error } = await supabase
        .from('prospects')
        .insert([formData]);

      if (error) {
        console.error('Erreur lors de la création:', error);
        alert('Erreur lors de la création du prospect');
      } else {
        setShowForm(false);
        resetForm();
        fetchProspects();
      }
    }
  };

  const handleEdit = (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      first_name: prospect.first_name || '',
      last_name: prospect.last_name || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      address: prospect.address || '',
      status: prospect.status || 'non_effectue',
      notes: prospect.notes || '',
      service_interested: prospect.service_interested || '',
      estimated_budget: prospect.estimated_budget || '',
      source: prospect.source || 'telephone'
    });
    setShowForm(true);
  };

  const handleConvertToClient = async (prospectId) => {
    if (!window.confirm('Voulez-vous convertir ce prospect en client ?')) {
      return;
    }

    const supabase = createClient();
    
    // Call the function to convert prospect to client
    const { error } = await supabase.rpc('convert_prospect_to_client', {
      prospect_id: prospectId
    });

    if (error) {
      console.error('Erreur lors de la conversion:', error);
      alert('Erreur lors de la conversion en client');
    } else {
      alert('Prospect converti en client avec succès !');
      fetchProspects();
    }
  };

  const updateStatus = async (id, newStatus) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('prospects')
      .update({ status: newStatus })
      .eq('id', id);

    if (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    } else {
      fetchProspects();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce prospect ? Cette action est irréversible.')) {
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('prospects')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du prospect');
      } else {
        fetchProspects();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du prospect');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      status: 'non_effectue',
      notes: '',
      service_interested: '',
      estimated_budget: '',
      source: 'telephone'
    });
    setEditingProspect(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'non_effectue': return 'bg-gray-100 text-gray-800';
      case 'en_attente': return 'bg-yellow-100 text-yellow-800';
      case 'valide': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'non_effectue': return <XCircle className="h-4 w-4" />;
      case 'en_attente': return <Clock className="h-4 w-4" />;
      case 'valide': return <CheckCircle className="h-4 w-4" />;
      default: return <XCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Chargement des prospects...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Prospects</h1>
            <p className="mt-2 text-gray-600">Gérez vos prospects de prospection téléphonique</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Ajouter un prospect
          </button>
        </div>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Tous les statuts</option>
            <option value="non_effectue">Non effectué</option>
            <option value="en_attente">En attente</option>
            <option value="valide">Validé</option>
          </select>
        </div>

        {showForm && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingProspect ? 'Modifier le prospect' : 'Nouveau prospect'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="non_effectue">Non effectué</option>
                    <option value="en_attente">En attente</option>
                    <option value="valide">Validé</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service intéressé
                  </label>
                  <input
                    type="text"
                    value={formData.service_interested}
                    onChange={(e) => setFormData({ ...formData, service_interested: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Budget estimé
                  </label>
                  <input
                    type="text"
                    value={formData.estimated_budget}
                    onChange={(e) => setFormData({ ...formData, estimated_budget: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingProspect ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {prospects.length === 0 ? (
              <li className="p-6 text-center text-gray-500">Aucun prospect trouvé.</li>
            ) : (
              prospects.map((prospect) => (
                <li key={prospect.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg font-semibold text-gray-900">
                          {prospect.first_name} {prospect.last_name}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(prospect.status)}`}>
                          {getStatusIcon(prospect.status)}
                          {prospect.status === 'non_effectue' && 'Non effectué'}
                          {prospect.status === 'en_attente' && 'En attente'}
                          {prospect.status === 'valide' && 'Validé'}
                        </span>
                        {prospect.converted_to_client && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Converti en client
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        {prospect.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${prospect.phone}`} className="text-primary-600 hover:underline">
                              {prospect.phone}
                            </a>
                          </p>
                        )}
                        {prospect.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${prospect.email}`} className="text-primary-600 hover:underline">
                              {prospect.email}
                            </a>
                          </p>
                        )}
                        {prospect.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {prospect.address}
                          </p>
                        )}
                        {prospect.service_interested && (
                          <p>Service: <span className="font-medium">{prospect.service_interested}</span></p>
                        )}
                        {prospect.estimated_budget && (
                          <p>Budget estimé: <span className="font-medium">{prospect.estimated_budget}</span></p>
                        )}
                        {prospect.notes && (
                          <p className="text-gray-700 mt-2">{prospect.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end space-y-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(prospect.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <div className="flex flex-col gap-2">
                        <select
                          value={prospect.status}
                          onChange={(e) => updateStatus(prospect.id, e.target.value)}
                          className="px-3 py-1 text-sm border rounded-md"
                        >
                          <option value="non_effectue">Non effectué</option>
                          <option value="en_attente">En attente</option>
                          <option value="valide">Validé</option>
                        </select>
                        {prospect.status === 'valide' && !prospect.converted_to_client && (
                          <button
                            onClick={() => handleConvertToClient(prospect.id)}
                            className="px-3 py-1 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                          >
                            Convertir en client
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(prospect)}
                          className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(prospect.id)}
                          className="px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                        >
                          <Trash2 className="h-4 w-4 inline mr-1" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}

